import React, { useState, useEffect, useRef } from 'react';
import { Link, NavLink, useNavigate, useLocation } from 'react-router-dom';
import SerialSearchBox from './SerialSearchBox';
import { useAuth } from '../context/AuthContext';
import { useBranding } from '../context/BrandingContext';
import { 
  LayoutDashboard, 
  Package, 
  Boxes, 
  Users, 
  MapPin, 
  Truck, 
  ClipboardList, 
  Factory, 
  Tag, 
  Bot,
  History,
  Settings,
  LogOut,
  Menu,
  Bug,
  FileText,
  Calendar,
  GitBranch,
  Stamp,
  KeyRound,
  ScanLine,
  Smartphone,
  Monitor,
  Bell,
  Inbox,
  CheckCheck,
  BarChart3,
  Swords,
  ChevronDown,
  ChevronRight,
  ShieldAlert,
  ShieldCheck,
  Library,
  TrendingDown,
  Trash2,
  X,
  ClipboardCheck,
  PackageOpen,
  Megaphone,
  Tv,
  ShoppingCart,
  Wrench,
  FlaskConical,
  ListChecks,
  FolderTree,
  Layers,
  Network,
  UsersRound,
  Boxes as PackageIcon
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { ScrollArea } from '../components/ui/scroll-area';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../components/ui/tooltip';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import PersonalAudio from './PersonalAudio';
import TeamDirectory from './TeamDirectory';
import VoiceCallManager from './VoiceCallManager';
import PrintCurrentViewFab from './PrintCurrentViewFab';

const BUILD = ['Admin', 'ProductionSupervisor'];
const BUILD_OPS = ['Admin', 'ProductionSupervisor', 'Operator'];
const CATALOG = ['Admin', 'InventoryManager', 'ProductionSupervisor'];
const RD = ['Admin', 'Engineer', 'ProductionSupervisor', 'InventoryManager'];
const STOCK = ['Admin', 'InventoryManager', 'ProductionSupervisor', 'Purchasing'];
const SELL = ['Admin', 'InventoryManager', 'ProductionSupervisor', 'Purchasing'];

// iter140 — sidebar follows the workflow: Design → Build (Assemblies → Products) → Shop floor → Stock → Buy → Sell & Ship → Insights
const navItems = [
  { path: '/', icon: LayoutDashboard, label: 'Dashboard', roles: ['Admin', 'InventoryManager', 'ProductionSupervisor', 'Purchasing', 'Operator'] },
  {
    section: 'Design', path: '/rd', icon: FlaskConical, label: 'R&D Projects', roles: RD,
    children: [
      { path: '/rd/my-tasks', icon: ListChecks, label: 'My Tasks', roles: RD },
      { path: '/rd/approvals', icon: ShieldCheck, label: 'Approvals', roles: RD },
      { path: '/rd/team-workload', icon: UsersRound, label: 'Team Workload', roles: ['Admin', 'ProductionSupervisor', 'InventoryManager'] },
    ],
  },
  {
    section: 'Build', path: '/assemblies', icon: Layers, label: 'Assemblies', roles: [...CATALOG, 'Engineer', 'Operator'], stream: 'assembly',
    children: [
      { path: '/assemblies/catalog', icon: Boxes, label: 'Assembly Catalog', roles: CATALOG },
      { path: '/assemblies/boms', icon: ClipboardList, label: 'Assembly BOMs', roles: BUILD },
      { path: '/assemblies/builds', icon: Factory, label: 'Assembly Builds', roles: BUILD_OPS },
    ],
  },
  {
    path: '/products', icon: Package, label: 'Products', roles: [...CATALOG, 'Engineer', 'Operator'], stream: 'product',
    children: [
      { path: '/products/families', icon: FolderTree, label: 'Families', roles: CATALOG },
      { path: '/products/catalog', icon: Package, label: 'Product Catalog', roles: CATALOG },
      { path: '/products/boms', icon: ClipboardList, label: 'Product BOMs', roles: BUILD },
      { path: '/products/builds', icon: Factory, label: 'Product Builds', roles: BUILD_OPS },
      { path: '/finished-units', icon: Tag, label: 'Finished Units', roles: BUILD_OPS },
      { path: '/structure', icon: Network, label: 'Product Structure', roles: [...CATALOG, 'Engineer'] },
    ],
  },
  {
    path: '/work-orders', icon: Factory, label: 'Shop Floor', roles: BUILD_OPS,
    children: [
      { path: '/rework', icon: ClipboardCheck, label: 'Rework Queue', roles: BUILD_OPS },
      { path: '/qc-templates', icon: ClipboardList, label: 'QC Templates', roles: BUILD },
      { path: '/unit-genealogy', icon: GitBranch, label: 'Unit Genealogy', roles: BUILD_OPS },
      { path: '/faceplates', icon: Stamp, label: 'Serials & Faceplates', roles: BUILD_OPS },
      { path: '/scan-serial', icon: ScanLine, label: 'Scan Serial', roles: BUILD_OPS },
      { path: '/shift-scheduling', icon: Calendar, label: 'Timecard', roles: BUILD_OPS },
    ],
  },
  {
    section: 'Stock', path: '/items', icon: Boxes, label: 'Item Master', roles: STOCK,
    children: [
      { path: '/items/dashboard', icon: LayoutDashboard, label: 'Item Dashboard', roles: STOCK },
      { path: '/data-integrity', icon: ShieldCheck, label: 'Data Integrity', roles: STOCK },
      { path: '/locations', icon: MapPin, label: 'Locations', roles: ['Admin', 'InventoryManager'] },
      { path: '/transactions', icon: History, label: 'Transactions', roles: ['Admin', 'InventoryManager'] },
      { path: '/item-categories', icon: Tag, label: 'Categories', roles: STOCK },
      { path: '/audits', icon: ClipboardCheck, label: 'Audits', roles: ['Admin', 'InventoryManager'] },
      { path: '/reports/inventory-variance', icon: TrendingDown, label: 'Variance Report', roles: CATALOG },
      { path: '/batch-lot-recall', icon: ShieldAlert, label: 'Lot Recall', roles: STOCK },
      { path: '/disposal-tickets', icon: Trash2, label: 'Disposal Tickets', roles: [...CATALOG, 'Operator'] },
      { path: '/labels', icon: Tag, label: 'Label Templates', roles: ['Admin', 'InventoryManager', 'ProductionSupervisor', 'Purchasing', 'Operator'] },
      { path: '/labels/library', icon: Library, label: 'Label Library', roles: ['Admin', 'InventoryManager', 'ProductionSupervisor', 'Purchasing', 'Operator'] },
    ],
  },
  {
    section: 'Buy', path: '/purchasing/pos', icon: ShoppingCart, label: 'Purchase Orders', roles: ['Admin', 'InventoryManager', 'Purchasing'],
  },
  {
    path: '/vendors', icon: Truck, label: 'Vendors', roles: ['Admin', 'Purchasing', 'InventoryManager'],
    children: [
      { path: '/open-orders', icon: PackageOpen, label: 'Open Orders', roles: STOCK },
      { path: '/rfx', icon: FileText, label: 'RFQ / RFP / RFI', roles: ['Admin', 'InventoryManager', 'Purchasing'] },
      { path: '/vendors/scorecard', icon: ShieldAlert, label: 'Vendor Scorecard', roles: ['Admin', 'InventoryManager', 'Purchasing'] },
      { path: '/admin/scorecard-schedule', icon: Calendar, label: 'Scorecard Schedule', roles: ['Admin', 'Purchasing', 'InventoryManager'] },
    ],
  },
  {
    section: 'Sell & Ship', path: '/orders', icon: ShoppingCart, label: 'Orders', roles: SELL,
    children: [
      { path: '/customers', icon: Users, label: 'Customers', roles: SELL },
      { path: '/orders/sales', icon: FileText, label: 'Sales Orders', roles: SELL },
      { path: '/orders/production', icon: ClipboardList, label: 'Production Orders', roles: BUILD },
      { path: '/orders/repairs', icon: Wrench, label: 'Lyphline Repair Orders', roles: CATALOG },
      { path: '/warranty', icon: ShieldCheck, label: 'Warranty', roles: STOCK },
    ],
  },
  {
    path: '/shipping', icon: Truck, label: 'Shipping', roles: [...CATALOG, 'Operator'],
    children: [
      { path: '/orders/packing', icon: PackageIcon, label: 'Packing Slips', roles: CATALOG },
      { path: '/orders/bol', icon: Truck, label: 'Bills of Lading', roles: CATALOG },
      { path: '/carriers', icon: Truck, label: 'Carriers', roles: ['Admin', 'InventoryManager'] },
      { path: '/shipping-analytics', icon: BarChart3, label: 'Shipping Analytics', roles: CATALOG },
    ],
  },
  {
    section: 'Insights & Admin', path: '/analytics', icon: BarChart3, label: 'Analytics', roles: CATALOG,
    children: [
      { path: '/reports', icon: FileText, label: 'Reports', roles: CATALOG },
      { path: '/reports/competitor-repairs', icon: Swords, label: 'Competitor Repairs', roles: CATALOG },
      { path: '/ai-assistant', icon: Bot, label: 'AI Assistant', roles: STOCK },
    ],
  },
  {
    path: '/admin/settings', icon: Settings, label: 'Admin Settings', roles: ['Admin'],
    children: [
      { path: '/users', icon: Users, label: 'Users', roles: ['Admin'] },
    ],
  },
];

// iter53: TV view launcher — small dropdown that lets the user pick a TV
// viewpoint (Production / Open Orders / Inventory Pulse) and opens /tv with
// the chosen view persisted to localStorage.
const TV_VIEWPOINTS = [
  { key: 'production', label: 'Production Floor' },
  { key: 'open_orders', label: 'Open Orders' },
  { key: 'inventory_pulse', label: 'Inventory Pulse' },
];

const TvViewLauncher = () => {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const STORAGE_KEY = 'app:tv_viewpoint';
  const current = (typeof window !== 'undefined' && window.localStorage.getItem(STORAGE_KEY)) || 'production';
  const launch = (key) => {
    try { window.localStorage.setItem(STORAGE_KEY, key); } catch {}
    setOpen(false);
    navigate(`/tv?view=${key}`);
  };
  return (
    <div className="relative">
      <Button
        variant="outline"
        size="sm"
        onClick={() => setOpen(o => !o)}
        data-testid="tv-launcher-trigger"
        className="rounded-sm gap-1"
        title="Open TV / kiosk dashboard"
      >
        <Tv className="w-4 h-4" />
        <span className="ml-0.5 hidden sm:inline">TV</span>
        <ChevronDown className="w-3 h-3 opacity-60" />
      </Button>
      {open && (
        <>
          <div className="fixed inset-0 z-30" onClick={() => setOpen(false)} />
          <div className="absolute right-0 mt-1 w-64 bg-white border border-slate-200 rounded-sm shadow-lg z-40" data-testid="tv-launcher-menu">
            <div className="text-[10px] uppercase tracking-wider text-slate-400 px-3 py-1.5 border-b border-slate-100">
              Pick TV Viewpoint
            </div>
            {TV_VIEWPOINTS.map(v => {
              const active = v.key === current;
              return (
                <button
                  key={v.key}
                  type="button"
                  onClick={() => launch(v.key)}
                  data-testid={`tv-launcher-option-${v.key}`}
                  className={`w-full text-left px-3 py-2 text-sm hover:bg-slate-50 flex items-center gap-2 ${active ? 'text-orange-600 font-medium' : 'text-slate-700'}`}
                >
                  <Tv className="w-3.5 h-3.5" />
                  {v.label}
                  {active && <span className="ml-auto text-[10px]">●</span>}
                </button>
              );
            })}
            <div className="border-t border-slate-100 px-3 py-1.5 text-[10px] text-slate-400">
              Choice is remembered on this device.
            </div>
          </div>
        </>
      )}
    </div>
  );
};

const Layout = ({ children }) => {
  const { user, logout, hasRole, api, token } = useAuth();
  const { branding } = useBranding();
  const appName = branding?.app_name || 'BOM Maestro';
  const appTagline = branding?.tagline || 'Inventory System';
  const brandLogo = branding?.show_logo_in_top_bar !== false ? (branding?.logo_data || '') : '';
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [mobileOptimized, setMobileOptimized] = useState(() => {
    return localStorage.getItem('mobileOptimized') === 'true';
  });
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [notificationOpen, setNotificationOpen] = useState(false);
  const [wsConnected, setWsConnected] = useState(false);
  const wsRef = useRef(null);
  // iter63 — inbox unread badge (separate from notifications bell)
  const [inboxUnread, setInboxUnread] = useState(0);
  // iter78c — Page Recall: count of unseen PA pages (delivered or missed)
  const [unseenPages, setUnseenPages] = useState(0);
  // Sidebar is always expanded - no collapse functionality
  const collapsed = false;

  // Track which parent groups are expanded in the sidebar
  const [expandedGroups, setExpandedGroups] = useState(() => {
    const initial = {};
    navItems.forEach(item => {
      if (item.children) {
        const onParent = location.pathname === item.path || location.pathname.startsWith(item.path + '/');
        const onChild = item.children.some(c => location.pathname === c.path || location.pathname.startsWith(c.path + '/'));
        initial[item.path] = onParent || onChild;
      }
    });
    return initial;
  });

  // Auto-expand a group when navigating into the parent or one of its children
  useEffect(() => {
    setExpandedGroups(prev => {
      const next = { ...prev };
      navItems.forEach(item => {
        if (item.children) {
          const onParent = location.pathname === item.path || location.pathname.startsWith(item.path + '/');
          const onChild = item.children.some(c => location.pathname === c.path || location.pathname.startsWith(c.path + '/'));
          if (onParent || onChild) next[item.path] = true;
        }
      });
      return next;
    });
  }, [location.pathname]);

  const toggleGroup = (path) => {
    setExpandedGroups(prev => ({ ...prev, [path]: !prev[path] }));
  };

  // WebSocket connection for real-time notifications
  useEffect(() => {
    if (!token) return;

    const connectWebSocket = () => {
      const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
      const host = window.location.host;
      const wsUrl = `${protocol}//${host}/api/ws/${token}`;

      try {
        wsRef.current = new WebSocket(wsUrl);

        wsRef.current.onopen = () => {
          console.log('WebSocket connected');
          setWsConnected(true);
        };

        wsRef.current.onmessage = (event) => {
          try {
            const data = JSON.parse(event.data);
            if (data.type === 'notification') {
              // Add new notification to the top of the list
              setNotifications(prev => [data.data, ...prev.slice(0, 9)]);
              setUnreadCount(prev => prev + 1);
              // Show toast for new notification
              import('sonner').then(({ toast }) => {
                toast.info(data.data.title, {
                  description: data.data.message
                });
              });
            } else if (data.type && (data.type.startsWith('pa:') || data.type.startsWith('tv:'))) {
              // iter61 — fan out to PersonalAudio + TvView listeners
              window.dispatchEvent(new CustomEvent('app:ws_event', { detail: data }));
              // iter78c — bump Page Recall counter when a PA arrives in realtime
              if (data.type === 'pa:tts' || data.type === 'pa:voice_invite') {
                setUnseenPages(prev => prev + 1);
              }
            } else if (data.type === 'message:new') {
              // iter63 — bump inbox unread badge instantly + fan out to InboxPage
              setInboxUnread(prev => prev + 1);
              window.dispatchEvent(new CustomEvent('app:ws_event', { detail: data }));
              import('sonner').then(({ toast }) => {
                toast.info(`New message from ${data.data?.from_name || 'Someone'}`, {
                  description: data.data?.subject || data.data?.preview,
                });
              });
            }
          } catch (error) {
            console.error('Failed to parse WebSocket message');
          }
        };

        wsRef.current.onclose = () => {
          setWsConnected(false);
          // Attempt reconnection after 5 seconds
          setTimeout(connectWebSocket, 5000);
        };

        wsRef.current.onerror = () => {
          console.error('WebSocket error');
        };
      } catch (error) {
        console.error('Failed to create WebSocket');
      }
    };

    connectWebSocket();

    return () => {
      if (wsRef.current) {
        wsRef.current.close();
      }
    };
  }, [token]);

  useEffect(() => {
    fetchNotifications();
    // Poll for new notifications every 30 seconds (backup for WebSocket)
    const interval = setInterval(fetchNotifications, 30000);
    return () => clearInterval(interval);
  }, []);

  // iter78c — refresh the unseen-pages count whenever the user navigates so
  // the Page Recall banner clears promptly after acknowledging on /inbox.
  useEffect(() => {
    const t = setTimeout(() => { fetchNotifications(); }, 600);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname, location.search]);

  // iter78d — announce missed pages with a short tone + TTS the first time the
  // Page Recall banner appears after this login session. Uses sessionStorage so
  // we only announce once per session even across remounts.
  const announceRef = useRef(false);
  useEffect(() => {
    if (unseenPages <= 0) return;
    if (announceRef.current) return;
    try {
      if (sessionStorage.getItem('app:pageRecallAnnounced') === '1') {
        announceRef.current = true;
        return;
      }
    } catch (e) { void e; }
    announceRef.current = true;
    try { sessionStorage.setItem('app:pageRecallAnnounced', '1'); } catch (e) { void e; }

    // Short attention beep via Web Audio API (no external assets needed)
    try {
      const AC = window.AudioContext || window.webkitAudioContext;
      if (AC) {
        const ctx = new AC();
        const beep = (freq, startOffset, dur) => {
          const o = ctx.createOscillator();
          const g = ctx.createGain();
          o.type = 'sine';
          o.frequency.value = freq;
          g.gain.value = 0.0001;
          o.connect(g).connect(ctx.destination);
          const t0 = ctx.currentTime + startOffset;
          o.start(t0);
          g.gain.exponentialRampToValueAtTime(0.18, t0 + 0.01);
          g.gain.exponentialRampToValueAtTime(0.0001, t0 + dur);
          o.stop(t0 + dur + 0.02);
        };
        beep(880, 0, 0.18);
        beep(1175, 0.22, 0.22);
      }
    } catch (e) { void e; }

    // Spoken summary via Web Speech API (best-effort — silently no-ops on unsupported browsers)
    try {
      if ('speechSynthesis' in window) {
        const speak = (text) => {
          const u = new window.SpeechSynthesisUtterance(text);
          u.rate = 1;
          u.pitch = 1;
          u.volume = 0.9;
          setTimeout(() => { try { window.speechSynthesis.speak(u); } catch (e) { void e; } }, 500);
        };
        // iter78f — fetch the most recent unseen page so the announcement
        // includes who paged and what they said.
        api().get('/audio/my-pages?limit=1&unread_only=true').then((r) => {
          const top = (r.data && r.data[0]) || null;
          if (top && top.text && top.text.trim()) {
            const sender = top.from_name || 'Unknown sender';
            const others = Math.max(0, unseenPages - 1);
            const tail = others === 0
              ? ''
              : others === 1
                ? ' One more page is waiting.'
                : ` ${others} more pages are waiting.`;
            speak(`Page from ${sender}: ${top.text}.${tail}`);
          } else {
            speak(unseenPages === 1
              ? 'You have one unseen page. Tap the red banner to review.'
              : `You have ${unseenPages} unseen pages. Tap the red banner to review.`);
          }
        }).catch(() => {
          speak(unseenPages === 1
            ? 'You have one unseen page. Tap the red banner to review.'
            : `You have ${unseenPages} unseen pages. Tap the red banner to review.`);
        });
      }
    } catch (e) { void e; }
  }, [unseenPages]);

  const fetchNotifications = async () => {
    try {
      const [notifRes, countRes, inboxRes, pagesRes] = await Promise.all([
        api().get('/notifications?unread_only=false').catch(() => ({ data: [] })),
        api().get('/notifications/unread-count').catch(() => ({ data: { unread_count: 0 } })),
        api().get('/messages/unread-count').catch(() => ({ data: { count: 0 } })),
        api().get('/audio/my-pages/unread-count').catch(() => ({ data: { unread: 0 } })),
      ]);
      setNotifications(notifRes.data.slice(0, 10)); // Show last 10
      setUnreadCount(countRes.data.unread_count);
      setInboxUnread(inboxRes.data.count || 0);
      setUnseenPages(pagesRes.data.unread || 0);
    } catch (error) {
      console.error('Failed to fetch notifications');
    }
  };

  const markAsRead = async (notificationId) => {
    try {
      await api().put(`/notifications/${notificationId}/read`);
      fetchNotifications();
    } catch (error) {
      console.error('Failed to mark as read');
    }
  };

  const markAllAsRead = async () => {
    try {
      await api().put('/notifications/mark-all-read');
      fetchNotifications();
    } catch (error) {
      console.error('Failed to mark all as read');
    }
  };

  // iter52
  const dismissNotification = async (notificationId, e) => {
    if (e) { e.stopPropagation(); }
    try {
      await api().delete(`/notifications/${notificationId}`);
      fetchNotifications();
    } catch (error) {
      console.error('Failed to dismiss notification');
    }
  };

  const clearAllNotifications = async () => {
    try {
      await api().delete('/notifications');
      fetchNotifications();
    } catch (error) {
      console.error('Failed to clear notifications');
    }
  };

  const handleNotificationClick = (notification) => {
    markAsRead(notification.id);
    if (notification.link) {
      navigate(notification.link);
    }
    setNotificationOpen(false);
  };

  const handleLogout = () => {
    try { sessionStorage.removeItem('app:pageRecallAnnounced'); } catch (e) { void e; }
    logout();
    navigate('/login');
  };

  const toggleMobileOptimized = () => {
    const newValue = !mobileOptimized;
    setMobileOptimized(newValue);
    localStorage.setItem('mobileOptimized', String(newValue));
    // Navigate to mobile page when enabling mobile mode
    if (newValue) {
      navigate('/mobile');
    } else if (location.pathname === '/mobile') {
      navigate('/');
    }
  };

  // If on mobile page, render without sidebar
  if (location.pathname === '/mobile') {
    return (
      <div className="min-h-screen bg-slate-50">
        <header className="h-14 bg-white border-b border-slate-200 flex items-center px-4 gap-4 sticky top-0 z-50">
          {brandLogo
            ? <img src={brandLogo} alt={appName} className="h-7 max-w-[120px] object-contain" data-testid="mobile-brand-logo" />
            : <span className="font-bold text-lg" data-testid="mobile-brand-name">{appName}</span>}
          <div className="flex-1" />
          <Button
            variant="outline"
            size="sm"
            onClick={toggleMobileOptimized}
            className="rounded-sm"
            data-testid="mobile-view-toggle"
          >
            <Monitor className="w-4 h-4 mr-1" />
            Full View
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={handleLogout}
            className="text-red-600"
          >
            <LogOut className="w-4 h-4" />
          </Button>
        </header>
        {/* iter78c — Page Recall banner on mobile view too */}
        {unseenPages > 0 && (
          <div className="w-full bg-red-600 text-white shadow-md flex animate-pulse text-sm" data-testid="page-recall-banner-mobile-row">
            <button
              type="button"
              onClick={() => navigate('/inbox?tab=pages')}
              className="flex-1 hover:bg-red-700 px-3 py-2 flex items-center justify-center gap-2"
              data-testid="page-recall-banner-mobile"
            >
              <Megaphone className="w-4 h-4" />
              <span className="font-semibold">{unseenPages} unseen page{unseenPages === 1 ? '' : 's'} — tap to review</span>
            </button>
            <button
              type="button"
              onClick={async () => {
                try {
                  await api().post('/audio/my-pages/mark-all-seen');
                  setUnseenPages(0);
                } catch (e) { /* noop */ }
              }}
              className="px-3 hover:bg-red-700 border-l border-red-400/60"
              title="Dismiss"
              data-testid="page-recall-dismiss-mobile"
              aria-label="Dismiss"
            >
              ✕
            </button>
          </div>
        )}
        <main className="p-3">
          {children}
        </main>
      </div>
    );
  }

  const filteredNavItems = navItems
    .map(item => {
      if (!item.children) {
        return hasRole(item.roles) ? item : null;
      }
      const hasParentAccess = hasRole(item.roles);
      const visibleChildren = item.children.filter(c => hasRole(c.roles));
      if (!hasParentAccess && visibleChildren.length === 0) return null;
      return { ...item, children: visibleChildren, _hasParentAccess: hasParentAccess };
    })
    .filter(Boolean);

  const SidebarContent = () => (
    <>
      <div className="p-4 border-b border-slate-200">
        <div className="flex items-center gap-2">
          {brandLogo ? (
            <img
              src={brandLogo}
              alt={appName}
              className="h-9 max-w-[180px] object-contain"
              data-testid="sidebar-brand-logo"
            />
          ) : (
            <>
              <div
                className="p-1.5 rounded-sm flex-shrink-0"
                style={{ background: 'var(--brand-primary, #ea580c)' }}
              >
                <Boxes className="w-5 h-5 text-white" />
              </div>
              <div className="overflow-hidden">
                <h1 className="font-bold text-lg truncate" data-testid="sidebar-brand-name">{appName}</h1>
                <p className="text-xs text-slate-500 truncate">{appTagline}</p>
              </div>
            </>
          )}
        </div>
      </div>

      <ScrollArea className="flex-1 py-2">
        <nav className="px-3 space-y-1">
          {filteredNavItems.map((item) => {
            const sectionHeader = item.section ? (
              <p className="px-3 pt-4 pb-1 text-[10px] font-semibold uppercase tracking-[0.14em] text-slate-400" data-testid={`nav-section-${item.section.toLowerCase().replace(/[^a-z]+/g, '-')}`}>{item.section}</p>
            ) : null;
            const streamDot = item.stream ? <span className={`ml-auto mr-1 w-1.5 h-1.5 rounded-full ${item.stream === 'assembly' ? 'bg-sky-500' : 'bg-orange-500'}`} /> : null;
            if (item.children && item.children.length > 0) {
              const expanded = !!expandedGroups[item.path];
              const isParentActive = location.pathname === item.path || location.pathname.startsWith(item.path + '/');
              const canNavigateParent = item._hasParentAccess;
              return (
                <div key={item.path}>
                  {sectionHeader}
                  <div className={`sidebar-item ${isParentActive && canNavigateParent ? 'active' : 'text-slate-600'} pr-1`}>
                    {canNavigateParent ? (
                      <NavLink
                        to={item.path}
                        onClick={() => setMobileOpen(false)}
                        className="flex items-center flex-1 min-w-0"
                        data-testid={`nav-${item.path.replace(/^\//, '').replace(/\//g, '-') || 'dashboard'}`}
                      >
                        <item.icon />
                        <span className="truncate">{item.label}</span>
                        {streamDot}
                      </NavLink>
                    ) : (
                      <button
                        type="button"
                        onClick={() => toggleGroup(item.path)}
                        className="flex items-center flex-1 min-w-0 text-left"
                        data-testid={`nav-${item.path.replace(/^\//, '').replace(/\//g, '-') || 'dashboard'}`}
                      >
                        <item.icon />
                        <span className="truncate">{item.label}</span>
                      </button>
                    )}
                    <button
                      type="button"
                      onClick={(e) => { e.preventDefault(); e.stopPropagation(); toggleGroup(item.path); }}
                      className={`p-1 rounded-sm flex-shrink-0 ${isParentActive && canNavigateParent ? 'hover:bg-orange-600' : 'hover:bg-slate-200'}`}
                      aria-label={expanded ? `Collapse ${item.label}` : `Expand ${item.label}`}
                      data-testid={`nav-${item.path.replace(/^\//, '').replace(/\//g, '-')}-toggle`}
                    >
                      {expanded
                        ? <ChevronDown className="w-4 h-4" />
                        : <ChevronRight className="w-4 h-4" />}
                    </button>
                  </div>
                  {expanded && (
                    <div className="mt-1 ml-4 pl-3 border-l border-slate-200 space-y-1">
                      {item.children.map(child => {
                        // iter130 — children can deep-link with ?query strings (R&D status filters).
                        // RR v6 NavLink auto-appends "active" to the DOM when className is a STRING
                        // and pathname matches — which made every R&D child light up at once.
                        // Returning a FUNCTION disables that auto-append and leaves styling entirely
                        // up to us; we then compare both pathname AND search for a precise match.
                        const [childPath, childSearch = ''] = child.path.split('?');
                        return (
                          <NavLink
                            key={child.path}
                            to={child.path}
                            onClick={() => setMobileOpen(false)}
                            className={() => {
                              const currentSearch = (location.search || '').replace(/^\?/, '');
                              const matches = location.pathname === childPath &&
                                              (childSearch === currentSearch);
                              return `sidebar-item ${matches ? 'active' : 'text-slate-600'} text-sm py-1.5`;
                            }}
                            data-testid={`nav-${child.path.replace(/^\//, '').replace(/\?/g, '-').replace(/[=&]/g, '-').replace(/\//g, '-')}`}
                          >
                            <child.icon />
                            <span>{child.label}</span>
                          </NavLink>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            }
            return (
              <div key={item.path}>
                {sectionHeader}
                <NavLink
                  to={item.path}
                  onClick={() => setMobileOpen(false)}
                  className={({ isActive }) =>
                    `sidebar-item ${isActive || location.pathname.startsWith(item.path + '/') ? 'active' : 'text-slate-600'}`
                  }
                  data-testid={`nav-${item.path.replace(/^\//, '').replace(/\//g, '-') || 'dashboard'}`}
                >
                  <item.icon />
                  <span>{item.label}</span>
                </NavLink>
              </div>
            );
          })}
        </nav>
      </ScrollArea>

      <div className="p-3 border-t border-slate-200">
        {user && (
          <NavLink
            to="/profile"
            onClick={() => setMobileOpen(false)}
            className={({ isActive }) =>
              `mb-2 block p-2 rounded-sm transition-colors ${isActive ? 'bg-orange-50 ring-1 ring-orange-200' : 'bg-slate-50 hover:bg-slate-100'}`
            }
            data-testid="sidebar-profile-link"
          >
            <p className="font-medium text-sm truncate">{user.full_name}</p>
            <p className="text-xs text-slate-500 flex items-center justify-between">
              <span>{user.role}</span>
              <span className="text-[10px] text-orange-600 hover:underline">View profile →</span>
            </p>
          </NavLink>
        )}
        <Button
          variant="ghost"
          size="sm"
          onClick={handleLogout}
          className="w-full text-slate-600 hover:text-red-600 hover:bg-red-50 rounded-sm justify-start"
          data-testid="logout-btn"
        >
          <LogOut className="w-5 h-5 mr-3" />
          <span>Sign Out</span>
        </Button>
      </div>
    </>
  );

  return (
    <div className="min-h-screen bg-slate-100 flex">
      {/* Desktop Sidebar - Always expanded */}
      <aside className="hidden lg:flex flex-col bg-white border-r border-slate-200 w-64">
        <SidebarContent />
      </aside>

      {/* Mobile Sidebar Overlay */}
      {mobileOpen && (
        <div 
          className="fixed inset-0 bg-black/50 z-40 lg:hidden"
          onClick={() => setMobileOpen(false)}
        />
      )}

      {/* Mobile Sidebar */}
      <aside 
        className={`fixed inset-y-0 left-0 w-64 bg-white border-r border-slate-200 z-50 transform transition-transform duration-200 lg:hidden ${
          mobileOpen ? 'translate-x-0' : '-translate-x-full'
        }`}
      >
        <SidebarContent />
      </aside>

      {/* Main Content */}
      <div className="flex-1 flex flex-col min-w-0">
        {/* Top Header */}
        <header className="h-14 bg-white border-b border-slate-200 flex items-center px-4 gap-4">
          <button 
            onClick={() => setMobileOpen(true)}
            className="lg:hidden p-2 hover:bg-slate-100 rounded-sm"
          >
            <Menu className="w-5 h-5" />
          </button>
          <SerialSearchBox />
          <Link to="/scan-serial" className="md:hidden p-2 text-slate-600" aria-label="Scan serial" data-testid="header-scan-serial-btn"><ScanLine className="w-5 h-5" /></Link>
          <div className="flex-1" />
          <div className="flex items-center gap-2">
            {/* Bug Report Button */}
            <TooltipProvider delayDuration={0}>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button 
                    variant="ghost" 
                    size="sm" 
                    onClick={() => navigate('/bugs')}
                    data-testid="bug-report-btn"
                  >
                    <Bug className="w-5 h-5" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent>Report a Bug</TooltipContent>
              </Tooltip>
            </TooltipProvider>

            {/* iter63 — Inbox button (separate from notifications bell) */}
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="relative"
                    onClick={() => navigate('/inbox')}
                    data-testid="inbox-button"
                  >
                    <Inbox className="w-5 h-5" />
                    {inboxUnread > 0 && (
                      <span className="absolute -top-1 -right-1 h-5 w-5 bg-blue-600 text-white text-xs rounded-full flex items-center justify-center" data-testid="inbox-unread-badge">
                        {inboxUnread > 9 ? '9+' : inboxUnread}
                      </span>
                    )}
                  </Button>
                </TooltipTrigger>
                <TooltipContent>Inbox · Messages & Pages</TooltipContent>
              </Tooltip>
            </TooltipProvider>

            {/* Notifications Bell */}
            <Popover open={notificationOpen} onOpenChange={setNotificationOpen}>
              <PopoverTrigger asChild>
                <Button variant="ghost" size="sm" className="relative" data-testid="notification-bell">
                  <Bell className="w-5 h-5" />
                  {unreadCount > 0 && (
                    <span className="absolute -top-1 -right-1 h-5 w-5 bg-red-500 text-white text-xs rounded-full flex items-center justify-center">
                      {unreadCount > 9 ? '9+' : unreadCount}
                    </span>
                  )}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-80 p-0" align="end">
                <div className="flex items-center justify-between p-3 border-b">
                  <h3 className="font-semibold">Notifications</h3>
                  <div className="flex items-center gap-1">
                    {unreadCount > 0 && (
                      <Button variant="ghost" size="sm" onClick={markAllAsRead} className="text-xs h-7 px-2" data-testid="notif-mark-all-read">
                        <CheckCheck className="w-4 h-4 mr-1" />
                        Mark all read
                      </Button>
                    )}
                    {notifications.length > 0 && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={clearAllNotifications}
                        className="text-xs h-7 px-2 text-red-600 hover:text-red-700 hover:bg-red-50"
                        data-testid="notif-clear-all"
                        title="Clear all notifications"
                      >
                        <Trash2 className="w-3.5 h-3.5 mr-1" />
                        Clear all
                      </Button>
                    )}
                  </div>
                </div>
                <ScrollArea className="h-[300px]">
                  {notifications.length > 0 ? (
                    <div className="divide-y">
                      {notifications.map(notif => (
                        <div
                          key={notif.id}
                          onClick={() => handleNotificationClick(notif)}
                          className={`p-3 cursor-pointer hover:bg-slate-50 transition-colors group ${!notif.read ? 'bg-blue-50' : ''}`}
                          data-testid={`notif-row-${notif.id}`}
                        >
                          <div className="flex items-start gap-2">
                            <div className={`w-2 h-2 rounded-full mt-2 flex-shrink-0 ${!notif.read ? 'bg-blue-500' : 'bg-transparent'}`} />
                            <div className="flex-1 min-w-0">
                              <p className="font-medium text-sm truncate">{notif.title}</p>
                              <p className="text-xs text-slate-500 line-clamp-2">{notif.message}</p>
                              <p className="text-xs text-slate-400 mt-1">
                                {new Date(notif.created_at).toLocaleString()}
                              </p>
                            </div>
                            <button
                              type="button"
                              onClick={(e) => dismissNotification(notif.id, e)}
                              className="opacity-0 group-hover:opacity-100 transition-opacity text-slate-400 hover:text-red-600 p-1 -mt-1 -mr-1"
                              data-testid={`notif-dismiss-${notif.id}`}
                              title="Dismiss"
                              aria-label="Dismiss notification"
                            >
                              <X className="w-3.5 h-3.5" />
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="flex flex-col items-center justify-center h-[200px] text-slate-500">
                      <Bell className="w-8 h-8 mb-2 opacity-30" />
                      <p className="text-sm">No notifications</p>
                    </div>
                  )}
                </ScrollArea>
              </PopoverContent>
            </Popover>

            {/* Mobile Optimized Toggle */}
            <TooltipProvider delayDuration={0}>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    variant={mobileOptimized ? "default" : "outline"}
                    size="sm"
                    onClick={toggleMobileOptimized}
                    className={`rounded-sm ${mobileOptimized ? 'bg-orange-500 hover:bg-orange-600' : ''}`}
                    data-testid="mobile-view-toggle"
                  >
                    <Smartphone className="w-4 h-4" />
                    <span className="ml-1 hidden sm:inline">
                      {mobileOptimized ? 'Mobile View' : 'Standard View'}
                    </span>
                  </Button>
                </TooltipTrigger>
                <TooltipContent>
                  {mobileOptimized ? 'Switch to Standard View' : 'Switch to Mobile-Optimized View'}
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
            <div className="hidden md:flex items-center gap-2">
              {/* iter53: TV mode launcher with viewpoint picker */}
              <TvViewLauncher />
              {/* iter63: Quick team directory popover for fast pages/notes */}
              <TeamDirectory />
              {/* iter61: Personal Audio (PA + Music) */}
              <PersonalAudio currentUser={user} />
            </div>
            <div className="flex items-center gap-2 text-sm">
              <span className="text-slate-500 hidden sm:inline">Logged in as</span>
              <span className="font-medium hidden sm:inline">{user?.full_name}</span>
              <span className="status-badge status-released hidden sm:inline">{user?.role}</span>
              <Link to="/change-password" className="p-1.5 text-slate-500 hover:text-slate-900" title="Change password" aria-label="Change password" data-testid="header-change-password-btn"><KeyRound className="w-4 h-4" /></Link>
            </div>
          </div>
        </header>

        {/* iter78c — Page Recall banner. Pulses red until unseen pages are acknowledged. */}
        {unseenPages > 0 && (
          <div className="w-full bg-red-600 text-white shadow-md flex items-stretch animate-pulse" data-testid="page-recall-banner-row">
            <button
              type="button"
              onClick={() => navigate('/inbox?tab=pages')}
              className="flex-1 hover:bg-red-700 px-4 py-2.5 flex items-center justify-center gap-3"
              data-testid="page-recall-banner"
            >
              <Megaphone className="w-5 h-5" />
              <span className="font-semibold tracking-wide">
                {unseenPages} unseen page{unseenPages === 1 ? '' : 's'} — tap to review
              </span>
              <span className="ml-2 inline-flex items-center px-2 py-0.5 rounded-full bg-white/20 text-xs font-mono">
                Inbox → Pages
              </span>
            </button>
            {/* iter78k — quick dismiss: marks all pages seen so the banner clears immediately */}
            <button
              type="button"
              onClick={async () => {
                try {
                  await api().post('/audio/my-pages/mark-all-seen');
                  setUnseenPages(0);
                } catch (e) { /* noop */ }
              }}
              className="px-3 hover:bg-red-700 border-l border-red-400/60"
              title="Mark all pages as seen and dismiss"
              data-testid="page-recall-dismiss"
              aria-label="Dismiss"
            >
              ✕
            </button>
          </div>
        )}

        {/* iter58: Severe weather banner — auto-shows when /tv/weather has_alert=true; dismissible per session */}
        <SevereWeatherBanner />

        {/* Page Content */}
        <main className={`flex-1 overflow-auto ${mobileOptimized ? 'p-3' : 'p-6'}`}>
          <div className={mobileOptimized ? 'mobile-optimized' : ''}>
            {children}
          </div>
        </main>
      </div>
      {/* iter65 — Voice call overlay (WebRTC two-way audio) */}
      <VoiceCallManager />
      {/* iter68 — Universal "Print this view" floating button */}
      <PrintCurrentViewFab />
    </div>
  );
};

// =================== Severe Weather Banner ===================
// Polls /api/tv/weather every 10 min. When `has_alert` is true, shows a red
// strip across the top of every authenticated page. The user can dismiss it
// for the current session via the X button (sessionStorage).
const SevereWeatherBanner = () => {
  const { api } = useAuth();
  const [weather, setWeather] = useState(null);
  const [dismissed, setDismissed] = useState(() => {
    try {
      return sessionStorage.getItem('app:weather-banner-dismissed') === '1';
    } catch { return false; }
  });
  useEffect(() => {
    let live = true;
    const fetchOnce = async () => {
      try {
        const r = await api().get('/tv/weather');
        if (live) setWeather(r.data);
      } catch {}
    };
    fetchOnce();
    const i = setInterval(fetchOnce, 10 * 60_000);
    return () => { live = false; clearInterval(i); };
  }, [api]);
  if (dismissed) return null;
  if (!weather || weather.enabled === false || !weather.has_alert) return null;
  const cur = weather.current || {};
  return (
    <div className="bg-red-600 text-white px-4 py-2 flex items-center gap-3 shadow-md" data-testid="severe-weather-banner">
      <ShieldAlert className="w-5 h-5 flex-shrink-0 animate-pulse" />
      <div className="flex-1 text-sm">
        <span className="uppercase tracking-widest text-[11px] mr-2 font-bold">Severe Weather</span>
        <span className="font-semibold">{cur.label}</span>
        <span className="opacity-80 ml-2">at {weather.location_label}</span>
        <span className="opacity-80 ml-2">· {Math.round(cur.temperature)}°{weather.units === 'celsius' ? 'C' : 'F'}</span>
        <span className="opacity-80 ml-2 hidden md:inline">— check shipping schedule + outdoor operations</span>
      </div>
      <button
        onClick={() => {
          try { sessionStorage.setItem('app:weather-banner-dismissed', '1'); } catch {}
          setDismissed(true);
        }}
        className="hover:bg-white/20 p-1 rounded-sm"
        title="Dismiss for this session"
        data-testid="severe-weather-dismiss"
      >
        <X className="w-4 h-4" />
      </button>
    </div>
  );
};

export default Layout;
